import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Trash2, Eye, FileText, Upload, Loader2, Download, CheckCircle, Clock, Send, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGREEMENT_TYPES = [
  { value: 'liability_waiver', label: 'Liability Waiver' },
  { value: 'coaching_agreement', label: 'Coaching Agreement' },
  { value: 'media_release', label: 'Media Release' },
  { value: 'other', label: 'Other' },
];

const REQUIRED_FOR_OPTIONS = [
  { value: 'event_registration', label: 'Event Registration' },
  { value: 'all_users', label: 'All Users' },
  { value: 'coaches', label: 'Coaches' },
  { value: 'youth_athletes', label: 'Youth Athletes' },
];

const AdminAgreementsTab = () => {
  const [agreements, setAgreements] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewAgreement, setViewAgreement] = useState(null);
  const [showSignatures, setShowSignatures] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    content: '',
    agreement_type: 'liability_waiver',
    required_for: [],
    pdf_url: '',
    file_key: '',
    active: true,
  });

  const fetchAgreements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/agreements`);
      setAgreements(res.data.agreements || []);
    } catch {
      toast.error('Failed to load agreements');
    }
    setLoading(false);
  };

  const fetchCoaches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`);
      const allUsers = res.data.users || res.data || [];
      setCoaches(allUsers.filter(u => u.role === 'coach'));
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchAgreements(); fetchCoaches(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim() || (!form.content.trim() && !form.pdf_url)) {
      toast.error('Title and content (or uploaded document) are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/agreements`, form);
      toast.success('Agreement created');
      setShowCreate(false);
      setForm({ title: '', description: '', content: '', agreement_type: 'liability_waiver', required_for: [], pdf_url: '', file_key: '', active: true });
      fetchAgreements();
    } catch {
      toast.error('Failed to create agreement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agreement? Existing signatures will be kept.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/agreements/${id}`);
      toast.success('Agreement deleted');
      fetchAgreements();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (ag) => {
    try {
      await axios.put(`${API_URL}/api/admin/agreements/${ag.id}`, { active: !ag.active });
      toast.success(ag.active ? 'Deactivated' : 'Activated');
      fetchAgreements();
    } catch {
      toast.error('Failed to update');
    }
  };

  const viewSignaturesAndAssignments = async (ag) => {
    setShowSignatures(ag);
    setLoadingSignatures(true);
    try {
      const [sigRes, assignRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/agreements/${ag.id}/signatures`),
        axios.get(`${API_URL}/api/admin/agreements/${ag.id}/assignments`)
      ]);
      setSignatures(sigRes.data.signatures || []);
      setAssignments(assignRes.data.assignments || []);
    } catch {
      toast.error('Failed to load data');
    }
    setLoadingSignatures(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/agreements/upload-doc`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        pdf_url: res.data.file_url,
        file_key: res.data.file_key || '',
        content: res.data.extracted_text || prev.content,
      }));
      if (res.data.extracted_text) {
        toast.success('Document uploaded and content extracted!');
      } else {
        toast.success('Document uploaded');
      }
    } catch {
      toast.error('Failed to upload document');
    }
    setUploading(false);
  };

  const openSendDialog = (ag) => {
    setShowSendDialog(ag);
    setSelectedCoaches([]);
  };

  const handleSend = async () => {
    if (selectedCoaches.length === 0) {
      toast.error('Select at least one coach');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/agreements/${showSendDialog.id}/send`, {
        coach_ids: selectedCoaches
      });
      toast.success(res.data.message);
      setShowSendDialog(null);
    } catch {
      toast.error('Failed to send agreement');
    }
    setSending(false);
  };

  const toggleCoachSelection = (id) => {
    setSelectedCoaches(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const selectAllCoaches = () => {
    if (selectedCoaches.length === coaches.length) {
      setSelectedCoaches([]);
    } else {
      setSelectedCoaches(coaches.map(c => c.id));
    }
  };

  const toggleRequiredFor = (val) => {
    setForm(prev => ({
      ...prev,
      required_for: prev.required_for.includes(val) ? prev.required_for.filter(v => v !== val) : [...prev.required_for, val]
    }));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const typeBadge = (type) => {
    const colors = { liability_waiver: 'bg-red-500/10 text-red-600 border-red-500/20', coaching_agreement: 'bg-blue-500/10 text-blue-600 border-blue-500/20', media_release: 'bg-purple-500/10 text-purple-600 border-purple-500/20', other: 'bg-muted text-muted-foreground' };
    const labels = { liability_waiver: 'Liability Waiver', coaching_agreement: 'Coaching Agreement', media_release: 'Media Release', other: 'Other' };
    return <Badge className={colors[type] || colors.other}>{labels[type] || type}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="admin-agreements-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{agreements.length} agreement{agreements.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setShowCreate(true)} className="rounded-full" data-testid="create-agreement-btn">
          <Plus className="w-4 h-4 mr-2" /> New Agreement
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : agreements.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No agreements yet. Create your first agreement template.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="hidden sm:table-cell">Required For</TableHead>
                <TableHead className="text-center">Signed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agreements.map(ag => (
                <TableRow key={ag.id} data-testid={`agreement-row-${ag.id}`}>
                  <TableCell>
                    <button onClick={() => setViewAgreement(ag)} className="text-left hover:text-primary">
                      <p className="font-medium">{ag.title}</p>
                      {ag.description && <p className="text-xs text-muted-foreground line-clamp-1">{ag.description}</p>}
                    </button>
                  </TableCell>
                  <TableCell>{typeBadge(ag.agreement_type)}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(ag.required_for || []).map(r => (
                        <Badge key={r} variant="outline" className="text-xs capitalize">{r.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <button onClick={() => viewSignaturesAndAssignments(ag)} className="text-primary hover:underline font-medium" data-testid={`view-signatures-${ag.id}`}>
                      {ag.signature_count || 0}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button onClick={() => handleToggleActive(ag)}>
                      {ag.active ? <Badge className="bg-green-500/10 text-green-600 border-green-500/20 cursor-pointer">Active</Badge> : <Badge variant="outline" className="cursor-pointer opacity-60">Inactive</Badge>}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setViewAgreement(ag)} className="text-muted-foreground hover:text-primary p-1" title="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => openSendDialog(ag)} className="text-muted-foreground hover:text-primary p-1" title="Send to coaches"><Send className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(ag.id)} className="text-muted-foreground hover:text-red-500 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* View Agreement Dialog */}
      <Dialog open={!!viewAgreement} onOpenChange={() => setViewAgreement(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">{viewAgreement?.title}</DialogTitle>
          </DialogHeader>
          {viewAgreement && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                {typeBadge(viewAgreement.agreement_type)}
                {viewAgreement.active ? <Badge className="bg-green-500/10 text-green-600">Active</Badge> : <Badge variant="outline">Inactive</Badge>}
                <button onClick={() => viewSignaturesAndAssignments(viewAgreement)} className="text-sm text-primary hover:underline">
                  {viewAgreement.signature_count || 0} signature(s) — View details
                </button>
              </div>
              {viewAgreement.description && <p className="text-sm text-muted-foreground">{viewAgreement.description}</p>}
              {viewAgreement.pdf_url && (
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/api/agreements/${viewAgreement.id}/download`);
                      window.open(res.data.url, '_blank');
                    } catch { toast.error('Failed to get download link'); }
                  }}
                  className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                >
                  <Download className="w-4 h-4" /> Download Original Document
                </button>
              )}
              <div className="bg-muted/30 p-4 rounded-lg border max-h-96 overflow-y-auto">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{viewAgreement.content || 'No text content available.'}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Agreement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">New Agreement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Upload Document (.docx or .pdf)</Label>
              <p className="text-xs text-muted-foreground mb-1">Upload a Word or PDF file — the text will be automatically extracted into the content box below.</p>
              <div className="flex items-center gap-3 mt-1">
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFileUpload} className="hidden" data-testid="doc-upload-input" />
                  <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                    <span>{uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-1" /> Upload File</>}</span>
                  </Button>
                </label>
                {form.pdf_url && <span className="text-xs text-green-600">File uploaded</span>}
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Liability Waiver 2026" data-testid="agreement-title-input" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.agreement_type} onValueChange={v => setForm(p => ({ ...p, agreement_type: v }))}>
                <SelectTrigger data-testid="agreement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AGREEMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Required For</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {REQUIRED_FOR_OPTIONS.map(opt => (
                  <button key={opt.value} type="button" onClick={() => toggleRequiredFor(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.required_for.includes(opt.value) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Agreement Content</Label>
              <p className="text-xs text-muted-foreground mb-1">This is the text users will see before signing. It auto-fills when you upload a Word document.</p>
              <Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={12} placeholder="Content will auto-populate when you upload a .docx file..." data-testid="agreement-content-textarea" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
              <Button onClick={handleCreate} className="rounded-full" data-testid="save-agreement-btn">
                <FileText className="w-4 h-4 mr-2" /> Create Agreement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Coaches Dialog */}
      <Dialog open={!!showSendDialog} onOpenChange={() => setShowSendDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-lg uppercase">Send Agreement</DialogTitle>
          </DialogHeader>
          {showSendDialog && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Send "<strong>{showSendDialog.title}</strong>" to coaches for signing. They will be notified via the platform and SMS.
              </p>
              <div className="flex items-center justify-between">
                <Label>Select Coaches</Label>
                <button onClick={selectAllCoaches} className="text-xs text-primary hover:underline">
                  {selectedCoaches.length === coaches.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border rounded-lg max-h-60 overflow-y-auto divide-y">
                {coaches.map(c => (
                  <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCoaches.includes(c.id)}
                      onChange={() => toggleCoachSelection(c.id)}
                      className="w-4 h-4 accent-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowSendDialog(null)} className="rounded-full">Cancel</Button>
                <Button onClick={handleSend} disabled={sending || selectedCoaches.length === 0} className="rounded-full">
                  {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send to {selectedCoaches.length} Coach{selectedCoaches.length !== 1 ? 'es' : ''}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signatures & Tracking Dialog */}
      <Dialog open={!!showSignatures} onOpenChange={() => setShowSignatures(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-lg uppercase">Tracking — {showSignatures?.title}</DialogTitle>
          </DialogHeader>
          {loadingSignatures ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : (
            <div className="space-y-6">
              {/* Assignments */}
              {assignments.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">Sent To ({assignments.length})</h3>
                  <div className="border rounded-lg divide-y">
                    {assignments.map(a => (
                      <div key={a.id} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">{a.user_name}</p>
                          <p className="text-xs text-muted-foreground">{a.user_email}</p>
                        </div>
                        <div className="text-right">
                          {a.signed ? (
                            <div className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4" /> Signed {a.signed_at ? formatDate(a.signed_at) : ''}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-amber-600 text-sm">
                              <Clock className="w-4 h-4" /> Awaiting
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">Sent {formatDate(a.sent_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Signatures */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">All Signatures ({signatures.length})</h3>
                {signatures.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No signatures yet.</p>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="hidden sm:table-cell">Guardian</TableHead>
                          <TableHead>Signed</TableHead>
                          <TableHead className="text-center">Signature</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {signatures.map(sig => (
                          <TableRow key={sig.id}>
                            <TableCell className="font-medium">{sig.signer_name}</TableCell>
                            <TableCell className="text-sm">{sig.user_email}</TableCell>
                            <TableCell className="hidden sm:table-cell text-sm">{sig.parent_guardian_name || '—'}</TableCell>
                            <TableCell className="text-sm">{formatDate(sig.signed_at)}</TableCell>
                            <TableCell className="text-center">
                              {(sig.signature_url || sig.signature_data) && (
                                <img src={sig.signature_url || sig.signature_data} alt="Sig" className="h-8 inline-block bg-white rounded border p-0.5" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAgreementsTab;
